import styles from "./main.module.scss";
import { useTranslation } from "react-i18next";
import {
  number,
  object
} from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Dropdown,
  DropdownItem
} from "../../shared/components/dropdown/dropdown";
import {
  useCallback,
  useEffect,
  useMemo, useState
} from "react";
import {
  useDispatch,
  useSelector
} from "react-redux";
import {
  AppDispatch,
  RootState
} from "../../store";
import { ReactComponent as WarningCircleSvg } from "../../shared/images/warningCircle.svg";
import arrowIcon from "../../shared/images/arrow2.svg";
import { Link } from "react-router-dom";
import { logout } from "../../store/slices/authSlice";
import lockIcon from "../../shared/images/lock.svg";
import { ChangePasswordModal } from "../../components/changePasswordModal/changePasswordModal";
import warningTriangleIcon from "../../shared/images/warningTriangle.svg";
import expiredIcon from "../../shared/images/expired.svg";
import {
  hasDatePassed,
  isLessThanOneMonthAway
} from "../../utils/date.utils";

const toolUrl = process.env.REACT_APP_TOOL_URL as string;

export const Main = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const { projects, isProjectAdmin, isTestToolAdmin, login } = useSelector((state: RootState) => state.authReducer);

  const [showChangePassword, setShowChangePassword] = useState<boolean>(false);

  const schema = object({
    projectIndex: number().required(t("common.notValid")),
  })
  const {
    control,
    handleSubmit,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange"
  });

  const onClickNext = useCallback(() => {
    handleSubmit((value) => {
      const project = projects[value.projectIndex];
      if (!!project && !!project.token) {
        window.location.href = `${toolUrl}#/?token=${encodeURIComponent(project.token)}&projectName=${encodeURIComponent(project.name)}&isSandbox=${+project.isSandbox}&login=${login ? encodeURIComponent(login) : ""}`;
      }
    })();
  }, [handleSubmit, projects, login]);

  const onKeyPress = useCallback((e: KeyboardEvent) => {
    if (e.key === "Enter") {
      onClickNext();
    }
  }, [onClickNext]);

  useEffect(() => {
    if (!showChangePassword) {
      window.addEventListener("keydown", onKeyPress);
    }

    return () => {
      window.removeEventListener('keydown', onKeyPress);
    }
  }, [showChangePassword, onKeyPress]);

  const dropdownProjects: Array<DropdownItem>  = useMemo(() => {
    return projects
      .map(((project, index) => {
        const expirationDate = new Date(project.passwordExpirationDate);
        return {
          id: index,
          name: (
            <div className={`${styles.dropdownItem} ${!project.token ? styles.disabled : ""}`}>
              <span title={project.name}>{project.name}</span>
              <div className={styles.info}>
                { !project.isActive && (<>
                  <span>{t("main.deactivated")}</span>
                  <img src={warningTriangleIcon} title={t("main.expiresDescription")} alt=""/>
                </>) }
                { project.isActive && isLessThanOneMonthAway(expirationDate) && (<>
                  <span>{`${t("main.expires")} ${expirationDate.toLocaleDateString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric'
                  })}`}</span>
                  <WarningCircleSvg title={t("main.expiresDescription")} />
                </>)}
                { project.isActive && hasDatePassed(expirationDate) && (<>
                  <span>{t("main.expired")}</span>
                  <img src={expiredIcon} title={t("main.expiresDescription")} alt=""/>
                </>)}
              </div>
            </div>
          ),
          disabled: !project.token
        }
      }));
  }, [projects]);

  return (
    <>
      <div className={styles.container}>
        {(isProjectAdmin || isTestToolAdmin) && (
          <Link to={"/admin"}
                className={styles.goToAdmin}>
            {t("main.goToAdminPanel")}
            <img src={arrowIcon} alt=""/>
          </Link>
        )}
        <div className={styles.title}>{t("main.title")}</div>
        <div className={styles.body}>
          <div className={styles.header}></div>
          <div className={styles.inputs}>
            {projects.length !== 0 ? (
              <div className={styles.inputContainer}>
                <span className={styles.label}>{t("main.project")}</span>
                <div className={styles.input}>
                  <Dropdown items={dropdownProjects}
                            selectedItemClass={styles.dropdownSelectedItem}
                            name={"projectIndex"}
                            control={control}/>
                </div>
              </div>
            ) : (
              <div className={styles.error}>
                <WarningCircleSvg />
                {t("main.error")}
              </div>
            )}
          </div>
        </div>
        <div className={styles.buttons}>
          <button className={styles.nextButton}
                  disabled={projects.length === 0}
                  onClick={onClickNext}>
            {t("main.next")}
          </button>
          <button className={styles.logoutButton}
                  onClick={() => dispatch(logout())}>
            {t("main.logout")}
          </button>
          <div className={styles.changePassword} onClick={() => setShowChangePassword(true)}>
            <img src={lockIcon} alt=""/>
            {t("main.changePassword")}
          </div>
        </div>
      </div>
      {showChangePassword && (<ChangePasswordModal onClose={() => setShowChangePassword(false)}/>)}
    </>

  )
}